import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { TenantDto } from '@/api/saas/types';
import { getAttributes, type WStudioAttribute } from '@/api/wsr/wstudioAttributes';
import { getGlobalConfig, type WStudioGlobalConfig } from '@/api/wsr/wstudioGlobalConfig';
import {
  getTenantConfig,
  putTenantConfig,
  type ContactFieldStatus,
  type WStudioField,
  type WStudioTenantConfig,
  type WStudioTenantUpdate,
} from '@/api/wsr/wstudioConfig';
import { wsrApiError } from '@/api/wsr/errors';
import type { PlatformTenantSummary, WStudioJobVerificationView } from '@/api/wsr/types';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { HelpPopover } from '@/components/ui';
import { HELP_SCOPE_BY_KEY, HELP_SCOPE_FALLBACK, type HelpScope } from '@/domain/globalConfigHelp';
import { jobTypeLabel } from '@/domain/registry';
import { RETIRED_KEYS } from '@/domain/retiredGlobalConfigKeys';
import { useCopy } from '@/hooks/useCopy';
import { DryRunPanel } from '../DryRunPanel';
import { ResetWStudioData } from './ResetWStudioData';

const DOMAIN_KEY = 'WStudio.domain';
const TOKEN_KEY = 'WStudio.token';
const STUDIO_ID_KEY = 'WStudio.Studioid';
const USER_ID_KEY = 'WStudio.report.checkin.userId';

const MEMBERSHIP_PREFIX = 'WStudio.membership.';
const PRODUCT_FAMILY_KEY = 'WStudio.membership.productFamilyByCategory';
const VALUE_MID_KEY = 'WStudio.membership.valueMidThreshold';
const VALUE_HIGH_KEY = 'WStudio.membership.valueHighThreshold';
const TENURE_ESTABLISHED_KEY = 'WStudio.membership.tenureEstablishedDays';
const TENURE_LOYAL_KEY = 'WStudio.membership.tenureLoyalDays';
const DAY_COUNT_KEYS = [
  'WStudio.membership.gracePeriodDays',
  'WStudio.membership.shortTermThresholdDays',
  'WStudio.membership.renewalDueSoonDays',
  TENURE_ESTABLISHED_KEY,
  TENURE_LOYAL_KEY,
  'WStudio.genesis.syncReadyCoverageDays',
];
const VALUE_THRESHOLD_KEYS = [VALUE_MID_KEY, VALUE_HIGH_KEY];

type TenantHelpScope = 'endpoint' | 'tenantOverride';

function tenantHelpScope(globalScope: HelpScope | undefined): TenantHelpScope | undefined {
  if (globalScope === 'endpoint') return 'endpoint';
  if (globalScope === 'platformDefault') return 'tenantOverride';
  return undefined;
}

interface ContactRow {
  id: number;
  key: string;
  value: string;
}

interface AddedMappingRow {
  id: number;
  attributeId: string;
  fieldKey: string;
  resolvedFieldId: string | null;
  status?: ContactFieldStatus;
}

export type WStudioSettingsSection = 'wstudio' | 'contacts' | 'advanced';

function shouldShowTopErrorAlert(
  section: WStudioSettingsSection,
  errorField: string | null,
): boolean {
  if (!errorField || errorField === 'contacts') return true;
  const onWstudio =
    errorField === 'domain' ||
    errorField === 'token' ||
    errorField === 'studioId' ||
    errorField === 'reportCheckinUserId' ||
    errorField === STUDIO_ID_KEY ||
    errorField === USER_ID_KEY;
  if (onWstudio) return section !== 'wstudio';
  if (errorField.startsWith('contact-') || errorField.startsWith('added-')) {
    return section !== 'contacts';
  }
  return section !== 'advanced';
}

interface WStudioSettingsProps {
  slug: string;
  tenant: TenantDto;
  summary: PlatformTenantSummary | null;
  onSummaryRefresh: () => void;
  section?: WStudioSettingsSection;
}

/**
 * WSRPDM-0051 — the tenant's email identity, read-only. §3 of the overview redesign
 * called this "configuration, not status" and moved it here; WSRPDM-0045 did the
 * Overview removal but left it unplaced. There is no tenant update endpoint in this
 * console (`src/api/saas/tenants.ts`), so these are plain text, never a `TextField`
 * or a disabled input — a disabled input would imply an editability that doesn't
 * exist here.
 *
 * A CONDITIONAL surface, not a literal move: the redesign's original complaint was
 * that these three fields sit unset and prominent. Rendering permanent "Not set"
 * rows here would relocate that complaint, not resolve it — so the whole section,
 * heading included, renders nothing unless at least one field has a real value.
 * Whitespace-only counts as unset; a partially-configured tenant shows only the
 * fields that have values, never an empty row for the rest.
 */
function EmailIdentitySection({ tenant }: { tenant: TenantDto }) {
  const { t } = useCopy();

  const emailFields = [
    {
      key: 'fromName',
      labelKey: 'emailIdentity.fromName',
      fallback: 'From name',
      value: tenant.emailFromName,
    },
    {
      key: 'fromAddress',
      labelKey: 'emailIdentity.fromAddress',
      fallback: 'From address',
      value: tenant.emailFromAddress,
    },
    {
      key: 'supportEmail',
      labelKey: 'emailIdentity.supportEmail',
      fallback: 'Support address',
      value: tenant.emailSupportEmail,
    },
  ].filter((field) => typeof field.value === 'string' && field.value.trim() !== '');

  if (emailFields.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
      <Typography variant="h2" sx={{ fontSize: '1.2rem', fontWeight: 600, mb: 2 }}>
        {t('emailIdentity.title', 'Email identity')}
      </Typography>
      <Stack spacing={1}>
        {emailFields.map((field) => (
          <Typography key={field.key} variant="body2">
            {t(field.labelKey, field.fallback)}: {field.value}
          </Typography>
        ))}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
        {t('emailIdentity.managedElsewhere', 'Configured outside this console.')}
      </Typography>
    </Paper>
  );
}

const labelFallbacks: Record<string, string> = {
  'WStudio.user.listWithFilters': 'User list URL',
  'WStudio.user.listWithFilters.max': 'User list maximum',
  'WStudio.user.listWithFilters.offset': 'User list offset',
  'WStudio.user.listWithFilters.filter': 'User list filter',
  'WStudio.sales.listSales': 'Tenant sales URL',
  'WStudio.sales.listForUser': 'Per-user sales URL',
  'WStudio.sales.listForUser.max': 'Per-user sales maximum',
  'WStudio.sales.listForUser.offset': 'Per-user sales offset',
  'WStudio.sales.listForUser.forceUpdate': 'Force update',
  'WStudio.report.checkin': 'Attendance report URL',
  'WStudio.product.listByType': 'Product catalogue URL',
  'WStudio.product.listByType.max': 'Product catalogue maximum',
  'WStudio.product.listByType.offset': 'Product catalogue offset',
  'WStudio.product.types': 'Product types to sync',
  'WStudio.membership.gracePeriodDays': 'Grace period (days)',
  'WStudio.membership.shortTermThresholdDays': 'Short-term threshold (days)',
  'WStudio.membership.renewalDueSoonDays': 'Renewal due soon (days)',
  [TENURE_ESTABLISHED_KEY]: 'Established tenure (days)',
  [TENURE_LOYAL_KEY]: 'Loyal tenure (days)',
  [VALUE_MID_KEY]: 'Mid-value threshold',
  [VALUE_HIGH_KEY]: 'High-value threshold',
  'WStudio.genesis.syncReadyCoverageDays': 'Attendance coverage required before sync (days)',
};

const contactStatusMeta: Record<
  ContactFieldStatus,
  { color: 'success' | 'default' | 'error'; key: string; fallback: string }
> = {
  RESOLVED_FROM_FIELD_KEY: {
    color: 'success',
    key: 'tenantDetail.settings.contacts.status.resolved',
    fallback: 'Resolved',
  },
  RESOLVED_FROM_FIELD_ID: {
    color: 'default',
    key: 'tenantDetail.settings.contacts.status.legacy',
    fallback: 'Resolved by ID',
  },
  UNRESOLVED_NOT_FOUND_IN_GHL: {
    color: 'error',
    key: 'tenantDetail.settings.contacts.status.notFound',
    fallback: 'Not found in GoHighLevel',
  },
  UNRESOLVED_UNKNOWN_ATTRIBUTE: {
    color: 'error',
    key: 'tenantDetail.settings.contacts.status.unknownAttribute',
    fallback: 'Unknown attribute',
  },
  UNSUPPORTED_CORE_FIELD_MAPPING: {
    color: 'default',
    key: 'tenantDetail.settings.contacts.status.retired',
    fallback: 'Retired',
  },
};

function findField(config: WStudioTenantConfig, key: string): WStudioField | undefined {
  return config.fields.find((field) => field.key === key);
}

function tenantValue(field: WStudioField | undefined): string {
  return field?.value ?? field?.derived ?? '';
}

function contactRows(config: WStudioTenantConfig, nextId: React.MutableRefObject<number>) {
  return Object.entries(config.contactMappings).map(([key, value]) => ({
    id: nextId.current++,
    key: key.startsWith('contact.') ? key : `contact.${key}`,
    value,
  }));
}

function VerificationSummary({ summary }: { summary: PlatformTenantSummary | null }) {
  const { t } = useCopy();
  if (!summary) return null;
  const jobs = summary.verification?.jobs ?? [];

  const statusText = (job: WStudioJobVerificationView | undefined) => {
    if (job?.stale) return t('tenantDetail.verification.stale', 'Stale — re-run needed');
    if (job?.status === 'SUCCESS') return t('tenantDetail.verification.verified', 'Verified');
    if (job?.status === 'FAILED' || job?.status === 'NOT_CONFIGURED') {
      return t('tenantDetail.verification.failed', 'Failed');
    }
    return t('tenantDetail.verification.notRun', 'Not run');
  };

  if (summary.wstudioConfigured) {
    return (
      <Alert severity="success">
        {t('tenantDetail.verification.allVerified', 'WStudio verified')}
      </Alert>
    );
  }

  return (
    <Alert severity="warning">
      <Typography variant="subtitle2">
        {t('tenantDetail.verification.notVerified', 'WStudio not configured / not verified')}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {t('tenantDetail.verification.prompt', 'Run all four dry-run tests to verify this tenant.')}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
        {(['SALES_SYNC', 'ATTENDANCE_SYNC', 'USER_SYNC', 'GHL_FIELD_CHECK'] as const).map(
          (jobType) => {
            const job = jobs.find((candidate) => candidate.jobType === jobType);
            return (
              <Chip
                key={jobType}
                size="small"
                label={`${jobTypeLabel(jobType, t)}: ${statusText(job)}${job?.verifiedAt && !job.stale ? ` · ${new Date(job.verifiedAt).toLocaleString()}` : ''}`}
                color={job?.status === 'SUCCESS' && !job.stale ? 'success' : 'default'}
                variant="outlined"
              />
            );
          },
        )}
      </Stack>
    </Alert>
  );
}

export function WStudioSettings({
  slug,
  tenant,
  summary,
  onSummaryRefresh,
  section = 'wstudio',
}: WStudioSettingsProps) {
  const { t } = useCopy();
  const nextContactId = useRef(1);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [config, setConfig] = useState<WStudioTenantConfig | null>(null);
  const [globalConfig, setGlobalConfig] = useState<WStudioGlobalConfig | null>(null);
  const [attributes, setAttributes] = useState<WStudioAttribute[]>([]);
  const [domain, setDomain] = useState('');
  const [studioId, setStudioId] = useState('');
  const [reportUserId, setReportUserId] = useState('');
  const [tokenValue, setTokenValue] = useState('');
  const [replacingToken, setReplacingToken] = useState(false);
  const [contacts, setContacts] = useState<ContactRow[]>([]);
  const [addedRows, setAddedRows] = useState<AddedMappingRow[]>([]);
  const [overrideValues, setOverrideValues] = useState<Record<string, string>>({});
  const [clearOverrides, setClearOverrides] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [errorField, setErrorField] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const hydrate = useCallback((next: WStudioTenantConfig) => {
    setConfig(next);
    setDomain(tenantValue(findField(next, DOMAIN_KEY)));
    setStudioId(tenantValue(findField(next, STUDIO_ID_KEY)));
    setReportUserId(tenantValue(findField(next, USER_ID_KEY)));
    setTokenValue('');
    setReplacingToken(false);
    setContacts(contactRows(next, nextContactId));
    setAddedRows(
      (next.contactFields ?? [])
        .filter((field) => field.scope === 'TENANT')
        .map((field) => ({
          id: nextContactId.current++,
          attributeId: field.attributeId ?? '',
          fieldKey: field.fieldKey ?? '',
          resolvedFieldId: field.resolvedFieldId,
          status: field.status,
        })),
    );
    setOverrideValues(
      Object.fromEntries(
        next.fields
          .filter(
            (field) =>
              field.key !== STUDIO_ID_KEY && field.key !== PRODUCT_FAMILY_KEY && field.overridden,
          )
          .map((field) => [field.key, field.derived ?? field.value ?? '']),
      ),
    );
    setClearOverrides(new Set());
    setErrorField(null);
    setErrorMessage(null);
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    Promise.all([
      getTenantConfig(slug),
      getGlobalConfig(),
      getAttributes().catch(() => ({ attributes: [] })),
    ])
      .then(([tenantConfig, defaults, attributeCatalog]) => {
        hydrate(tenantConfig);
        setGlobalConfig(defaults);
        setAttributes(attributeCatalog.attributes);
      })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  }, [hydrate, slug]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getTenantConfig(slug),
      getGlobalConfig(),
      getAttributes().catch(() => ({ attributes: [] })),
    ])
      .then(([tenantConfig, defaults, attributeCatalog]) => {
        if (cancelled) return;
        hydrate(tenantConfig);
        setGlobalConfig(defaults);
        setAttributes(attributeCatalog.attributes);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hydrate, slug]);

  const markChanged = () => {
    setSaved(false);
    setErrorField(null);
    setErrorMessage(null);
  };

  if (loading) return <LoadingSpinner />;
  if (loadError || !config || !globalConfig) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={load}>
            {t('common.retry', 'Retry')}
          </Button>
        }
      >
        {t('tenantDetail.settings.loadError', 'Could not load WStudio settings.')}
      </Alert>
    );
  }

  const token = findField(config, TOKEN_KEY);
  const advancedFields = config.fields.filter(
    (field) =>
      field.key !== STUDIO_ID_KEY &&
      field.key !== PRODUCT_FAMILY_KEY &&
      !RETIRED_KEYS.has(field.key) &&
      (field.kind === 'value' || field.kind === 'param' || field.kind === 'url'),
  );
  const globalValues = Object.fromEntries(globalConfig.keys.map(({ key, value }) => [key, value]));
  const catalogMode = Array.isArray(config.contactFields);
  const globalContactFields = (config.contactFields ?? []).filter(
    (field) => field.scope === 'GLOBAL',
  );
  const sortedGlobalContactFields = [
    ...globalContactFields.filter((field) => field.key === 'contact.wstudio_id'),
    ...globalContactFields.filter((field) => field.key !== 'contact.wstudio_id'),
  ];
  const hasUnresolvedRequiredField = globalContactFields.some(
    (field) => field.required && field.status.startsWith('UNRESOLVED'),
  );
  const mappableAttributes = attributes.filter(
    (attribute) => attribute.source === 'USER_ATTRIBUTE',
  );

  const hasGlobalDefault = (field: WStudioField) =>
    Object.prototype.hasOwnProperty.call(globalValues, field.key);
  const globalDefault = (field: WStudioField) => globalValues[field.key] ?? '';
  const effectiveDefault = (field: WStudioField) => {
    const value = globalDefault(field);
    if (field.kind === 'url' && value.startsWith('/')) return `${domain}${value}`;
    return hasGlobalDefault(field) ? value : (field.derived ?? '');
  };
  const overrideSeed = (field: WStudioField) =>
    field.kind === 'url'
      ? effectiveDefault(field)
      : hasGlobalDefault(field)
        ? globalDefault(field)
        : (field.derived ?? '');

  const addContact = () => {
    markChanged();
    setContacts((current) => [
      ...current,
      { id: nextContactId.current++, key: 'contact.', value: '' },
    ]);
  };

  const addAddedMapping = () => {
    markChanged();
    setAddedRows((current) => [
      ...current,
      {
        id: nextContactId.current++,
        attributeId: '',
        fieldKey: '',
        resolvedFieldId: null,
      },
    ]);
  };

  const selectAttribute = (row: AddedMappingRow, attributeId: string) => {
    markChanged();
    const previousAttribute = mappableAttributes.find(
      (attribute) => attribute.id === row.attributeId,
    );
    const nextAttribute = mappableAttributes.find((attribute) => attribute.id === attributeId);
    setAddedRows((current) =>
      current.map((item) => {
        if (item.id !== row.id) return item;
        const shouldPrefill =
          !item.fieldKey.trim() || item.fieldKey === previousAttribute?.suggestedKey;
        return {
          ...item,
          attributeId,
          fieldKey: shouldPrefill ? (nextAttribute?.suggestedKey ?? item.fieldKey) : item.fieldKey,
        };
      }),
    );
  };

  const handleSave = async () => {
    if (!domain.trim()) {
      setErrorField('domain');
      setErrorMessage(t('tenantDetail.settings.validation.domain', 'Domain is required.'));
      return;
    }
    let contactMappings: Record<string, string> | undefined;
    if (catalogMode) {
      const missingAttribute = addedRows.find((row) => !row.attributeId);
      if (missingAttribute) {
        setErrorField(`added-${missingAttribute.id}`);
        setErrorMessage(
          t(
            'tenantDetail.settings.validation.attributeRequired',
            'Choose a WStudio user attribute for every mapping.',
          ),
        );
        return;
      }
    } else {
      const mappingRows = contacts.filter(({ key, value }) => key.trim() || value.trim());
      const invalidContact = mappingRows.find(
        ({ key, value }) => !/^contact\.[A-Za-z0-9._-]+$/.test(key.trim()) || !value.trim(),
      );
      if (invalidContact) {
        setErrorField(`contact-${invalidContact.id}`);
        setErrorMessage(
          t(
            'tenantDetail.settings.validation.contact',
            'Enter a contact.<field> key and field ID for every mapping.',
          ),
        );
        return;
      }
      const normalizedKeys = mappingRows.map(({ key }) => key.trim().replace(/^contact\./, ''));
      if (new Set(normalizedKeys).size !== normalizedKeys.length) {
        setErrorField('contacts');
        setErrorMessage(
          t('tenantDetail.settings.validation.duplicateContact', 'Contact keys must be unique.'),
        );
        return;
      }
      contactMappings = Object.fromEntries(
        mappingRows.map(({ key, value }) => [key.trim().replace(/^contact\./, ''), value.trim()]),
      );
    }
    const invalidUrl = advancedFields.find(
      (field) =>
        field.kind === 'url' &&
        field.key in overrideValues &&
        !/^https?:\/\//i.test(overrideValues[field.key] ?? ''),
    );
    if (invalidUrl) {
      setErrorField(invalidUrl.key);
      setErrorMessage(
        t('tenantDetail.settings.validation.fullUrl', 'Enter a complete http(s) URL.'),
      );
      return;
    }

    // Mirror the server's field-scoped 400s for §J2 as fast client-side feedback; the server
    // remains authoritative. An unset value threshold (no default, deliberately) or an unset
    // tenure/day-count default is a valid state and must never be treated as an error here.
    const overriddenRaw = (key: string): string | null => {
      if (!(key in overrideValues) || clearOverrides.has(key)) return null;
      return overrideValues[key] ?? '';
    };
    const effectiveNumericValue = (key: string): number | null => {
      const overriding = key in overrideValues && !clearOverrides.has(key);
      const raw = overriding
        ? (overrideValues[key] ?? '')
        : (findField(config, key)?.derived ?? '');
      const trimmed = raw.trim();
      if (trimmed === '') return null;
      const parsed = Number(trimmed);
      return Number.isFinite(parsed) ? parsed : null;
    };

    for (const key of DAY_COUNT_KEYS) {
      const raw = overriddenRaw(key);
      if (raw === null) continue;
      const trimmed = raw.trim();
      const parsed = Number(trimmed);
      if (trimmed === '' || !Number.isInteger(parsed) || parsed < 0) {
        setErrorField(key);
        setErrorMessage(
          t(
            'tenantDetail.settings.validation.nonNegativeInt',
            'Enter a whole number of 0 or more.',
          ),
        );
        return;
      }
    }
    for (const key of VALUE_THRESHOLD_KEYS) {
      const raw = overriddenRaw(key);
      if (raw === null) continue;
      const trimmed = raw.trim();
      if (trimmed === '') continue; // blank while overriding = intentionally unset
      const parsed = Number(trimmed);
      if (!Number.isFinite(parsed) || parsed < 0) {
        setErrorField(key);
        setErrorMessage(
          t('tenantDetail.settings.validation.nonNegativeNumber', 'Enter an amount of 0 or more.'),
        );
        return;
      }
    }
    const midValue = effectiveNumericValue(VALUE_MID_KEY);
    const highValue = effectiveNumericValue(VALUE_HIGH_KEY);
    if (midValue !== null && highValue !== null && midValue > highValue) {
      setErrorField(VALUE_MID_KEY);
      setErrorMessage(
        t(
          'tenantDetail.settings.validation.valueThresholdOrder',
          'The mid-value threshold must be less than or equal to the high-value threshold.',
        ),
      );
      return;
    }
    const establishedValue = effectiveNumericValue(TENURE_ESTABLISHED_KEY);
    const loyalValue = effectiveNumericValue(TENURE_LOYAL_KEY);
    if (establishedValue !== null && loyalValue !== null && establishedValue > loyalValue) {
      setErrorField(TENURE_ESTABLISHED_KEY);
      setErrorMessage(
        t(
          'tenantDetail.settings.validation.tenureOrder',
          'The established-tenure threshold must be less than or equal to the loyal-tenure threshold.',
        ),
      );
      return;
    }

    const body: WStudioTenantUpdate = {
      domain: domain.trim(),
      studioId: studioId.trim(),
      reportCheckinUserId: reportUserId.trim(),
      overrides: advancedFields
        .filter((field) => field.key in overrideValues && !clearOverrides.has(field.key))
        .map((field) => ({ key: field.key, value: overrideValues[field.key] ?? '' })),
      clearOverrides: [...clearOverrides],
    };
    if (catalogMode) {
      body.contactFieldMappings = addedRows
        .filter((row) => row.attributeId)
        .map((row) =>
          row.fieldKey.trim()
            ? { attributeId: row.attributeId, fieldKey: row.fieldKey.trim() }
            : { attributeId: row.attributeId },
        );
    } else {
      body.contactMappings = contactMappings ?? {};
    }
    if (tokenValue.trim()) body.token = tokenValue;

    setSaving(true);
    setSaved(false);
    setErrorField(null);
    setErrorMessage(null);
    try {
      const updated = await putTenantConfig(slug, body);
      hydrate(updated);
      setSaved(true);
      onSummaryRefresh();
    } catch (error) {
      const apiError = wsrApiError(error);
      const field = apiError?.field;
      if (field?.startsWith('overrides')) {
        const index = Number(field.match(/\d+/)?.[0]);
        const overrideKey = body.overrides?.[index]?.key;
        const isKnownOverrideKey = advancedFields.some(
          (candidate) => candidate.key === overrideKey,
        );
        setErrorField(overrideKey && isKnownOverrideKey ? overrideKey : null);
      } else if (field === 'domain' || field === DOMAIN_KEY) {
        setErrorField('domain');
      } else if (field === 'token' || field === TOKEN_KEY) {
        setReplacingToken(true);
        setErrorField('token');
      } else if (field === 'studioId' || field === STUDIO_ID_KEY) {
        setErrorField('studioId');
      } else if (field === 'reportCheckinUserId' || field === USER_ID_KEY) {
        setErrorField('reportCheckinUserId');
      } else if (field?.startsWith('contactFieldMappings')) {
        const index = Number(field.match(/\d+/)?.[0]);
        const row = Number.isInteger(index) ? addedRows[index] : undefined;
        setErrorField(row ? `added-${row.id}` : null);
      } else if (field?.startsWith('contactMappings')) {
        const contactKey = field.match(/contactMappings(?:\.|\[)([^\].]+)/)?.[1];
        const renderedKey = contactKey ? `contact.${contactKey}` : null;
        setErrorField(
          renderedKey && contacts.some((row) => row.key === renderedKey) ? renderedKey : null,
        );
      } else if (field?.startsWith('contact.')) {
        setErrorField(contacts.some((row) => row.key === field) ? field : null);
      } else if (advancedFields.some((candidate) => candidate.key === field)) {
        setErrorField(field ?? null);
      } else {
        setErrorField(null);
      }
      setErrorMessage(
        apiError?.message ??
          t('tenantDetail.settings.saveError', 'Could not save WStudio settings.'),
      );
    } finally {
      setSaving(false);
    }
  };

  const renderAdvancedField = (field: WStudioField) => {
    const overriding = field.key in overrideValues && !clearOverrides.has(field.key);
    const pendingClear = clearOverrides.has(field.key);
    const hasError = errorField === field.key;
    const copyKey = field.key.replaceAll('.', '_');
    const label = t(
      `tenantDetail.settings.fields.${copyKey}`,
      labelFallbacks[field.key] ?? field.key,
    );
    // Deliberately share Global config's reviewed corpus: these are the same WStudio settings.
    const detail = t(`globalConfig.fields.${copyKey}.detail`, '');
    const helpScope = tenantHelpScope(HELP_SCOPE_BY_KEY[field.key]);
    return (
      <Paper key={field.key} variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
              <Typography variant="subtitle2">{label}</Typography>
              {detail && helpScope ? (
                <HelpPopover
                  label={label}
                  scope={t(
                    `help.scope.${helpScope}`,
                    helpScope === 'endpoint' ? HELP_SCOPE_FALLBACK.endpoint : 'Tenant override',
                  )}
                  body={detail}
                />
              ) : null}
            </Stack>
            <Chip
              size="small"
              label={
                overriding
                  ? t('tenantDetail.settings.advanced.overriding', 'Overriding')
                  : t('tenantDetail.settings.advanced.default', 'Default')
              }
              color={overriding ? 'primary' : 'default'}
            />
          </Box>
          {overriding ? (
            <TextField
              label={
                field.kind === 'url'
                  ? t('tenantDetail.settings.advanced.fullUrl', 'Complete URL override')
                  : (labelFallbacks[field.key] ?? field.key)
              }
              value={overrideValues[field.key]}
              onChange={(event) => {
                markChanged();
                setOverrideValues((current) => ({
                  ...current,
                  [field.key]: event.target.value,
                }));
              }}
              error={hasError}
              helperText={hasError ? errorMessage : undefined}
              fullWidth
            />
          ) : (
            <TextField
              label={t('tenantDetail.settings.advanced.derived', 'Derived value')}
              value={pendingClear ? effectiveDefault(field) : (field.derived ?? '')}
              slotProps={{ input: { readOnly: true } }}
              fullWidth
            />
          )}
          <Typography variant="caption" color="text.secondary">
            {t('tenantDetail.settings.advanced.defaultValue', 'default: {{value}}', {
              value: globalDefault(field),
            })}
          </Typography>
          {field.kind === 'url' ? (
            <Typography variant="caption" color="text.secondary">
              {t(
                'tenantDetail.settings.advanced.urlHelp',
                'A URL override replaces the tenant domain and global path completely.',
              )}
            </Typography>
          ) : null}
          {overriding ? (
            <Button
              size="small"
              sx={{ alignSelf: 'flex-start' }}
              onClick={() => {
                markChanged();
                setOverrideValues((current) => {
                  const next = { ...current };
                  delete next[field.key];
                  return next;
                });
                setClearOverrides((current) => new Set(current).add(field.key));
              }}
            >
              {t('tenantDetail.settings.advanced.revert', 'Revert to default')}
            </Button>
          ) : (
            <Button
              size="small"
              sx={{ alignSelf: 'flex-start' }}
              onClick={() => {
                markChanged();
                setClearOverrides((current) => {
                  const next = new Set(current);
                  next.delete(field.key);
                  return next;
                });
                setOverrideValues((current) => ({
                  ...current,
                  [field.key]: overrideSeed(field),
                }));
              }}
            >
              {t('tenantDetail.settings.advanced.override', 'Override')}
            </Button>
          )}
        </Stack>
      </Paper>
    );
  };

  const membershipFields = advancedFields.filter((field) =>
    field.key.startsWith(MEMBERSHIP_PREFIX),
  );
  const restAdvancedFields = advancedFields.filter(
    (field) => !field.key.startsWith(MEMBERSHIP_PREFIX),
  );
  const dayCountMembershipFields = membershipFields.filter(
    (field) => field.key !== VALUE_MID_KEY && field.key !== VALUE_HIGH_KEY,
  );
  const midField = membershipFields.find((field) => field.key === VALUE_MID_KEY);
  const highField = membershipFields.find((field) => field.key === VALUE_HIGH_KEY);
  const isThresholdSet = (field: WStudioField | undefined, key: string): boolean => {
    if (!field) return false;
    const overriding = key in overrideValues && !clearOverrides.has(key);
    const raw = overriding ? (overrideValues[key] ?? '') : (field.derived ?? '');
    return raw.trim() !== '';
  };
  const showValuePartial =
    isThresholdSet(midField, VALUE_MID_KEY) !== isThresholdSet(highField, VALUE_HIGH_KEY);

  return (
    <Stack spacing={3}>
      {section === 'wstudio' ? <VerificationSummary summary={summary} /> : null}
      {errorMessage && shouldShowTopErrorAlert(section, errorField) ? (
        <Alert severity="error">{errorMessage}</Alert>
      ) : null}
      {saved ? (
        <Alert severity="success">
          <Typography variant="body2">
            {t('tenantDetail.settings.saveSuccess', 'WStudio settings saved.')}
          </Typography>
          <Typography variant="body2">
            {t(
              'tenantDetail.settings.saveSuccessReverify',
              'Configuration changed — re-run the four dry-run tests to re-verify this tenant.',
            )}
          </Typography>
        </Alert>
      ) : null}

      <Box
        component="form"
        aria-label={t('tenantDetail.settings.formAriaLabel', 'WStudio tenant settings form')}
        sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}
        onSubmit={(event) => {
          event.preventDefault();
          void handleSave();
        }}
      >
        {section === 'wstudio' ? (
          <>
            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
              <Typography variant="h2" sx={{ fontSize: '1.2rem', fontWeight: 600, mb: 2 }}>
                {t('tenantDetail.settings.title', 'Tenant WStudio settings')}
              </Typography>
              <Grid container spacing={2} data-testid="tenant-settings-grid">
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label={t('tenantDetail.settings.domain.label', 'Domain')}
                    helperText={
                      errorField === 'domain'
                        ? errorMessage
                        : t(
                            'tenantDetail.settings.domain.help',
                            'Required WStudio tenant base URL.',
                          )
                    }
                    value={domain}
                    onChange={(event) => {
                      markChanged();
                      setDomain(event.target.value);
                    }}
                    error={errorField === 'domain'}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <Stack spacing={1}>
                    <Typography variant="body2">
                      {t('tenantDetail.settings.token.label', 'WStudio API token')} —{' '}
                      {token?.valueSet
                        ? t('tenantDetail.settings.token.set', 'Set · {{suffix}}', {
                            suffix: token.valueSuffix ?? '',
                          })
                        : t('tenantDetail.settings.token.notSet', 'Not set')}
                    </Typography>
                    {replacingToken || !token?.valueSet ? (
                      <TextField
                        label={t('tenantDetail.settings.token.replacement', 'New token')}
                        type="password"
                        value={tokenValue}
                        onChange={(event) => {
                          markChanged();
                          setTokenValue(event.target.value);
                        }}
                        error={errorField === 'token'}
                        helperText={
                          errorField === 'token'
                            ? errorMessage
                            : t(
                                'tenantDetail.settings.token.help',
                                'Write-only. Blank preserves the stored token.',
                              )
                        }
                        fullWidth
                      />
                    ) : (
                      <Button
                        size="small"
                        sx={{ alignSelf: 'flex-start' }}
                        onClick={() => setReplacingToken(true)}
                      >
                        {t('tenantDetail.settings.token.replace', 'Replace')}
                      </Button>
                    )}
                  </Stack>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label={t('tenantDetail.settings.studioId.label', 'Studio ID')}
                    value={studioId}
                    onChange={(event) => {
                      markChanged();
                      setStudioId(event.target.value);
                    }}
                    error={errorField === 'studioId' || errorField === STUDIO_ID_KEY}
                    helperText={
                      errorField === 'studioId' || errorField === STUDIO_ID_KEY
                        ? errorMessage
                        : t('tenantDetail.settings.studioId.help', 'WStudio Studioid header value.')
                    }
                    fullWidth
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                  <TextField
                    label={t('tenantDetail.settings.reportUserId.label', 'Report check-in user ID')}
                    value={reportUserId}
                    onChange={(event) => {
                      markChanged();
                      setReportUserId(event.target.value);
                    }}
                    error={errorField === 'reportCheckinUserId' || errorField === USER_ID_KEY}
                    helperText={
                      errorField === 'reportCheckinUserId' || errorField === USER_ID_KEY
                        ? errorMessage
                        : t(
                            'tenantDetail.settings.reportUserId.help',
                            'User ID used to generate attendance reports.',
                          )
                    }
                    fullWidth
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
              <DryRunPanel
                slug={slug}
                verification={summary?.verification}
                onRunComplete={onSummaryRefresh}
              />
            </Paper>
          </>
        ) : null}

        {section === 'contacts' ? (
          <Box>
            <Typography variant="h2" sx={{ fontSize: '1.2rem', fontWeight: 600, mb: 2 }}>
              {t('tenantDetail.settings.contacts.title', 'Contact field mappings')}
            </Typography>
            {catalogMode ? (
              <Stack spacing={3}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {t('tenantDetail.settings.contacts.catalogTitle', 'Standard mappings')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t(
                      'tenantDetail.settings.contacts.catalogHelp',
                      'Managed globally and applied to every tenant. The GoHighLevel field ID is resolved automatically.',
                    )}
                  </Typography>
                  {hasUnresolvedRequiredField ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      {t(
                        'tenantDetail.settings.contacts.unresolvedWarning',
                        'One or more required mappings could not be resolved. Create the matching custom field in GoHighLevel, then re-run the dry-run tests.',
                      )}
                    </Alert>
                  ) : null}
                  <TableContainer>
                    <Table
                      size="small"
                      aria-label={t(
                        'tenantDetail.settings.contacts.catalogTitle',
                        'Standard mappings',
                      )}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            {t('tenantDetail.settings.contacts.col.key', 'Contact key')}
                          </TableCell>
                          <TableCell>
                            {t('tenantDetail.settings.contacts.col.attribute', 'WStudio attribute')}
                          </TableCell>
                          <TableCell>
                            {t(
                              'tenantDetail.settings.contacts.col.fieldId',
                              'GoHighLevel field ID',
                            )}
                          </TableCell>
                          <TableCell>
                            {t('tenantDetail.settings.contacts.col.status', 'Status')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedGlobalContactFields.map((field) => {
                          const status = contactStatusMeta[field.status];
                          return (
                            <TableRow key={field.key}>
                              <TableCell>{field.key}</TableCell>
                              <TableCell>
                                {field.attributeLabel ?? field.attributeId ?? '—'}
                              </TableCell>
                              <TableCell>{field.resolvedFieldId ?? '—'}</TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  color={status.color}
                                  label={t(status.key, status.fallback)}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>

                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {t('tenantDetail.settings.contacts.addedTitle', 'Additional mappings')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t(
                      'tenantDetail.settings.contacts.addedHelp',
                      'Pick a WStudio user attribute. The GoHighLevel field ID is discovered when you save.',
                    )}
                  </Typography>
                  <Stack spacing={1}>
                    {addedRows.map((row) => {
                      const rowError = errorField === `added-${row.id}`;
                      const retired = row.status === 'UNSUPPORTED_CORE_FIELD_MAPPING';
                      return (
                        <Stack key={row.id} spacing={0.5}>
                          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                            <TextField
                              select
                              label={t(
                                'tenantDetail.settings.contacts.attributeLabel',
                                'WStudio user attribute',
                              )}
                              value={row.attributeId}
                              onChange={(event) => selectAttribute(row, event.target.value)}
                              error={rowError && !row.attributeId}
                              helperText={rowError && !row.attributeId ? errorMessage : undefined}
                              disabled={mappableAttributes.length === 0}
                              fullWidth
                            >
                              {mappableAttributes.map((attribute) => (
                                <MenuItem key={attribute.id} value={attribute.id}>
                                  {attribute.label}
                                </MenuItem>
                              ))}
                            </TextField>
                            <TextField
                              label={t(
                                'tenantDetail.settings.contacts.fieldKeyLabel',
                                'GoHighLevel field key',
                              )}
                              value={row.fieldKey}
                              onChange={(event) => {
                                markChanged();
                                setAddedRows((current) =>
                                  current.map((item) =>
                                    item.id === row.id
                                      ? { ...item, fieldKey: event.target.value }
                                      : item,
                                  ),
                                );
                              }}
                              error={rowError && Boolean(row.attributeId)}
                              helperText={rowError && row.attributeId ? errorMessage : undefined}
                              fullWidth
                            />
                            <TextField
                              label={t(
                                'tenantDetail.settings.contacts.col.fieldId',
                                'GoHighLevel field ID',
                              )}
                              value={row.resolvedFieldId ?? '—'}
                              slotProps={{ input: { readOnly: true } }}
                              fullWidth
                            />
                            {retired ? (
                              <Chip
                                size="small"
                                color={contactStatusMeta.UNSUPPORTED_CORE_FIELD_MAPPING.color}
                                label={t(
                                  contactStatusMeta.UNSUPPORTED_CORE_FIELD_MAPPING.key,
                                  contactStatusMeta.UNSUPPORTED_CORE_FIELD_MAPPING.fallback,
                                )}
                                sx={{ alignSelf: { xs: 'flex-start', md: 'center' } }}
                              />
                            ) : null}
                            <Button
                              type="button"
                              color="error"
                              onClick={() => {
                                markChanged();
                                setAddedRows((current) =>
                                  current.filter((item) => item.id !== row.id),
                                );
                              }}
                            >
                              {t('tenantDetail.settings.contacts.remove', 'Remove')}
                            </Button>
                          </Stack>
                          {retired ? (
                            <Typography variant="caption" color="text.secondary">
                              {t(
                                'tenantDetail.settings.contacts.status.retiredHelp',
                                'Core-field mappings are no longer supported. This mapping is ignored — remove it.',
                              )}
                            </Typography>
                          ) : null}
                        </Stack>
                      );
                    })}
                    <Button
                      type="button"
                      onClick={addAddedMapping}
                      disabled={mappableAttributes.length === 0}
                      sx={{ alignSelf: 'flex-start' }}
                    >
                      {t('tenantDetail.settings.contacts.add', 'Add mapping')}
                    </Button>
                    {mappableAttributes.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        {t(
                          'tenantDetail.settings.contacts.attributesUnavailable',
                          'The WStudio attribute list is unavailable, so new mappings cannot be added right now.',
                        )}
                      </Typography>
                    ) : null}
                  </Stack>
                </Box>
              </Stack>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t(
                    'tenantDetail.settings.contacts.help',
                    'Map open-ended contact.* keys to GHL field IDs.',
                  )}
                </Typography>
                <Stack spacing={1}>
                  {contacts.map((row) => (
                    <Stack key={row.id} direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                      <TextField
                        label={t('tenantDetail.settings.contacts.key', 'Contact key')}
                        value={row.key}
                        onChange={(event) => {
                          markChanged();
                          setContacts((current) =>
                            current.map((item) =>
                              item.id === row.id ? { ...item, key: event.target.value } : item,
                            ),
                          );
                        }}
                        error={errorField === `contact-${row.id}` || errorField === row.key}
                        helperText={
                          errorField === `contact-${row.id}` || errorField === row.key
                            ? errorMessage
                            : undefined
                        }
                        fullWidth
                      />
                      <TextField
                        label={t('tenantDetail.settings.contacts.value', 'GHL field ID')}
                        value={row.value}
                        onChange={(event) => {
                          markChanged();
                          setContacts((current) =>
                            current.map((item) =>
                              item.id === row.id ? { ...item, value: event.target.value } : item,
                            ),
                          );
                        }}
                        fullWidth
                      />
                      <Button
                        color="error"
                        onClick={() => {
                          markChanged();
                          setContacts((current) => current.filter((item) => item.id !== row.id));
                        }}
                      >
                        {t('tenantDetail.settings.contacts.remove', 'Remove')}
                      </Button>
                    </Stack>
                  ))}
                  <Button onClick={addContact} sx={{ alignSelf: 'flex-start' }}>
                    {t('tenantDetail.settings.contacts.add', 'Add mapping')}
                  </Button>
                </Stack>
              </>
            )}
          </Box>
        ) : null}

        {section === 'advanced' ? (
          <Box>
            <Typography variant="h2" sx={{ fontSize: '1.2rem', fontWeight: 600, mb: 2 }}>
              {t('tenantDetail.settings.advanced.title', 'Advanced — override global config')}
            </Typography>
            <Stack spacing={2}>
              {membershipFields.length > 0 ? (
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {t('tenantDetail.settings.membership.title', 'Membership & classification')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t(
                      'tenantDetail.settings.membership.help',
                      'These settings are read together to classify members for marketing.',
                    )}
                  </Typography>
                  <Stack spacing={2}>
                    {dayCountMembershipFields.map((field) => renderAdvancedField(field))}
                    {midField || highField ? (
                      <Box>
                        <Stack
                          direction={{ xs: 'column', md: 'row' }}
                          spacing={2}
                          sx={{ '& > *': { flex: 1 } }}
                        >
                          {midField ? renderAdvancedField(midField) : null}
                          {highField ? renderAdvancedField(highField) : null}
                        </Stack>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block', mt: 1 }}
                        >
                          {t(
                            'tenantDetail.settings.membership.valueHelp',
                            'Both thresholds must be set for value classification to run. Leaving them blank is a valid choice — no value classification is produced.',
                          )}
                        </Typography>
                        {showValuePartial ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: 'block' }}
                          >
                            {t(
                              'tenantDetail.settings.membership.valuePartial',
                              'Only one threshold is set, so value classification stays off.',
                            )}
                          </Typography>
                        ) : null}
                      </Box>
                    ) : null}
                  </Stack>
                </Box>
              ) : null}
              {restAdvancedFields.map((field) => renderAdvancedField(field))}
            </Stack>
          </Box>
        ) : null}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving
              ? t('tenantDetail.settings.saving', 'Saving…')
              : t('tenantDetail.settings.save', 'Save WStudio settings')}
          </Button>
        </Box>
      </Box>

      {section === 'wstudio' ? (
        <>
          <EmailIdentitySection tenant={tenant} />
          <ResetWStudioData slug={slug} onLoad={load} onSummaryRefresh={onSummaryRefresh} />
        </>
      ) : null}
    </Stack>
  );
}
